import { ReactNode } from 'react';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import { OptionRowsSkeleton } from '@/components/core/skeletons/skeletons';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { UIPhoneInput } from '@/components/core/phone-input/phone-input';
import type { Value } from 'react-phone-number-input';
import { SERVICE_DESK_PHONE_COUNTRIES } from '../application-utils';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';

interface FieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  /** Marks the label with `*`; set it to match the form's schema. */
  required?: boolean;
}

/** The field's label, marked with `*` when the field is required. */
const FieldLabel = ({ label, required }: { label: string; required?: boolean }) => (
  <FormLabel>
    {label}
    {required && <span className="text-error"> *</span>}
  </FormLabel>
);

/** Text-like input (`text`, `email`, `tel`, `date`, ...) bound to a react-hook-form field. */
export const TextField = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  required,
  type = 'text',
  min,
  max,
}: FieldProps<T> & { type?: string; min?: string; max?: string }) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FieldLabel label={label} required={required} />
        <FormControl>
          {type === 'date' ? (
            <DatePicker
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            />
          ) : (
            <Input
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              {...field}
              value={field.value ?? ''}
            />
          )}
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);

/** International phone number field (country picker + formatting), matching the rest of the app. */
export const PhoneField = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  required,
}: FieldProps<T>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FieldLabel label={label} required={required} />
        <FormControl>
          <UIPhoneInput
            {...field}
            value={field.value ?? ''}
            onChange={(value?: Value) => field.onChange(value ?? '')}
            placeholder={placeholder}
            disabled={disabled}
            defaultCountry="BD"
            countries={SERVICE_DESK_PHONE_COUNTRIES}
            countryCallingCodeEditable={false}
            international
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);

export const TextareaField = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  required,
  rows = 3,
}: FieldProps<T> & { rows?: number }) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FieldLabel label={label} required={required} />
        <FormControl>
          <Textarea
            rows={rows}
            placeholder={placeholder}
            disabled={disabled}
            {...field}
            value={field.value ?? ''}
          />
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export const SelectField = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  required,
  options,
  isLoading = false,
}: FieldProps<T> & {
  options: SelectOption[];
  /** The options come from the API and are still loading. */
  isLoading?: boolean;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FieldLabel label={label} required={required} />
        <Select value={field.value ?? ''} onValueChange={field.onChange} disabled={disabled}>
          <FormControl>
            <SelectTrigger>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            {isLoading && <OptionRowsSkeleton />}
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);

/** Two-column responsive grid the legacy forms used for their field groups. */
export const FieldGrid = ({ children }: { children: ReactNode }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
);
